import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const SystemConfiguration = () => {
  const [activeTab, setActiveTab] = useState('payment');

  const configSections = [
    {
      id: 'payment',
      label: 'Payment Settings',
      icon: 'CreditCard',
      description: 'Configure payment processing and fees'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: 'Bell',
      description: 'Email and SMS notification templates'
    },
    {
      id: 'security',
      label: 'Security',
      icon: 'Shield',
      description: 'Authentication and security policies'
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: 'Zap',
      description: 'Third-party service configurations'
    }
  ];

  const paymentSettings = [
    {
      label: "Platform Fee (%)",
      value: "2.5",
      description: "Percentage fee charged on each transaction"
    },
    {
      label: "Minimum Payout",
      value: "$50.00",
      description: "Minimum amount for event manager payouts"
    },
    {
      label: "Payout Schedule",
      value: "Weekly",
      description: "Frequency of automatic payouts"
    },
    {
      label: "Refund Window (days)",
      value: "7",
      description: "Days allowed for ticket refunds"
    }
  ];

  const notificationTemplates = [
    {
      type: "Order Confirmation",
      status: "Active",
      lastModified: "2025-09-25",
      opens: "94.2%"
    },
    {
      type: "Event Reminder",
      status: "Active",
      lastModified: "2025-09-20",
      opens: "87.5%"
    },
    {
      type: "Refund Processed",
      status: "Active",
      lastModified: "2025-09-18",
      opens: "91.8%"
    },
    {
      type: "Password Reset",
      status: "Active",
      lastModified: "2025-09-15",
      opens: "96.1%"
    }
  ];

  const securityPolicies = [
    {
      policy: "Password Requirements",
      status: "Enabled",
      description: "Minimum 8 characters, mixed case, numbers, symbols"
    },
    {
      policy: "Two-Factor Authentication",
      status: "Optional",
      description: "SMS and authenticator app support"
    },
    {
      policy: "Session Timeout",
      status: "30 minutes",
      description: "Automatic logout after inactivity"
    },
    {
      policy: "Failed Login Lockout",
      status: "5 attempts",
      description: "Account lockout after failed attempts"
    }
  ];

  const integrations = [
    {
      service: "Stripe",
      status: "Connected",
      lastSync: "2025-09-29 01:30",
      health: "Healthy"
    },
    {
      service: "Twilio SMS",
      status: "Connected",
      lastSync: "2025-09-29 01:25",
      health: "Healthy"
    },
    {
      service: "Resend Email",
      status: "Connected",
      lastSync: "2025-09-29 01:20",
      health: "Healthy"
    },
    {
      service: "PostHog Analytics",
      status: "Connected",
      lastSync: "2025-09-29 01:15",
      health: "Warning"
    }
  ];

  const renderPaymentSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {paymentSettings?.map((setting, index) => (
          <div key={index} className="bg-muted rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-foreground">{setting?.label}</label>
              <Button variant="ghost" size="sm" iconName="Edit" />
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{setting?.value}</div>
            <p className="text-xs text-muted-foreground">{setting?.description}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-end space-x-3">
        <Button variant="outline">Reset to Defaults</Button>
        <Button variant="default">Save Changes</Button>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div className="space-y-6">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Template
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Last Modified
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Open Rate
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {notificationTemplates?.map((template, index) => (
              <tr key={index}>
                <td className="px-4 py-3 text-sm font-medium text-foreground">{template?.type}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-success text-success-foreground">
                    {template?.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{template?.lastModified}</td>
                <td className="px-4 py-3 text-sm text-foreground">{template?.opens}</td>
                <td className="px-4 py-3">
                  <div className="flex space-x-2">
                    <Button variant="ghost" size="sm" iconName="Edit">Edit</Button>
                    <Button variant="ghost" size="sm" iconName="Eye">Preview</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        {securityPolicies?.map((policy, index) => (
          <div key={index} className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <h4 className="text-sm font-medium text-foreground">{policy?.policy}</h4>
              <p className="text-xs text-muted-foreground">{policy?.description}</p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-foreground">{policy?.status}</span>
              <Button variant="ghost" size="sm" iconName="Settings" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end">
        <Button variant="default">Update Security Settings</Button>
      </div>
    </div>
  );

  const renderIntegrations = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {integrations?.map((integration, index) => (
          <div key={index} className="p-4 bg-muted rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground">{integration?.service}</h4>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                integration?.health === 'Healthy' ? 'bg-success text-success-foreground' :
                integration?.health === 'Warning' ? 'bg-warning text-warning-foreground' :
                'bg-error text-error-foreground'
              }`}>
                {integration?.health}
              </span>
            </div>
            <div className="space-y-2 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>Status:</span>
                <span className="text-foreground">{integration?.status}</span>
              </div>
              <div className="flex justify-between">
                <span>Last Sync:</span>
                <span className="text-foreground">{integration?.lastSync}</span>
              </div>
            </div>
            <div className="mt-3 flex space-x-2">
              <Button variant="ghost" size="sm" iconName="RefreshCw">Sync</Button>
              <Button variant="ghost" size="sm" iconName="Settings">Configure</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'payment': return renderPaymentSettings();
      case 'notifications': return renderNotifications();
      case 'security': return renderSecurity();
      case 'integrations': return renderIntegrations();
      default: return renderPaymentSettings();
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground mb-2">System Configuration</h3>
        <p className="text-sm text-muted-foreground">Manage platform settings and integrations</p>
      </div>
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar Navigation */}
        <div className="lg:w-64 border-b lg:border-b-0 lg:border-r border-border">
          <nav className="p-4 space-y-1">
            {configSections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => setActiveTab(section?.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === section?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={section?.icon} size={18} className="mr-3" />
                <div className="text-left">
                  <div>{section?.label}</div>
                  <div className="text-xs opacity-75">{section?.description}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SystemConfiguration;